import type { FavoriteKey, FavoriteView } from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";

/**
 * HTTP-клиент избранного §6.6 запросного канала §7.6. Маршруты — `/api/favorites`
 * одним путём для всех трёх операций (design.md решение 3): запись не несёт
 * идентификатора, и `DELETE` адресует её тем же ключом, каким `POST` создаёт.
 * Тот же стиль различения исходов статусом, что у `pins-api.ts`.
 */

const FAVORITES_BASE = "/api/favorites";

/**
 * Рынок ключа записи — только значения контракта `MarketKind` или пустота:
 * биржа и рынок §6.6 либо конкретны, либо не заданы вовсе, и произвольная
 * строка здесь — признак негодного тела, а не третьего рынка (review
 * stage6-favorites, п. 7).
 */
function isMarketKind(value: unknown): value is "spot" | "perp" | null {
  return value === "spot" || value === "perp" || value === null;
}

function isFavoriteView(value: unknown): value is FavoriteView {
  return (
    isRecord(value) &&
    (typeof value.exchange === "string" || value.exchange === null) &&
    isMarketKind(value.market) &&
    typeof value.base === "string" &&
    typeof value.created_at_ms === "number"
  );
}

/** Список избранного §6.6 предъявившего сессию пользователя, целиком. */
export type FavoritesListResult =
  | { status: "ok"; favorites: FavoriteView[] }
  | { status: "unauthorized" }
  | { status: "unavailable" };

export async function fetchFavorites(): Promise<FavoritesListResult> {
  let response: Response;
  try {
    response = await fetch(FAVORITES_BASE, {
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (!response.ok) {
    console.error("список избранного отклонён демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!Array.isArray(body) || !body.every(isFavoriteView)) {
      return { status: "unavailable" };
    }
    return { status: "ok", favorites: body };
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * Исход добавления записи избранного. Демон отвечает `200`, не `201`:
 * повторное добавление той же записи идемпотентно (спека `user-favorites`,
 * design.md решение 2) — клиенту незачем различать эти два случая статусом.
 */
export type FavoriteAddResult =
  | { status: "ok"; favorite: FavoriteView }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "unavailable" };

export async function addFavorite(key: FavoriteKey): Promise<FavoriteAddResult> {
  let response: Response;
  try {
    response = await fetch(FAVORITES_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(key),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (!response.ok) {
    console.error("избранное не добавлено демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!isFavoriteView(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", favorite: body };
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * Исход удаления записи избранного. Демон отвечает `204` и на отсутствующую
 * запись (design.md решение 3, спека `user-favorites`): ключ описывает
 * желаемое состояние «такой записи нет», и оно уже достигнуто — «нет такого»
 * здесь не исход вовсе.
 */
export type FavoriteRemoveResult =
  | { status: "ok" }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "unavailable" };

export async function removeFavorite(key: FavoriteKey): Promise<FavoriteRemoveResult> {
  let response: Response;
  try {
    response = await fetch(FAVORITES_BASE, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(key),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (response.status === 204) {
    return { status: "ok" };
  }
  console.error("избранное не снято демоном", response.status);
  return { status: "unavailable" };
}
